'use client';

import { useCallback, useEffect, useState } from 'react';
import { Activity } from 'react';

import { useTranslations } from 'next-intl';

import WalletBlackIcon from '@/assets/icons/wallet-black.svg';
import WalletConnectBlue from '@/assets/icons/walletConnectBlue.svg';
import { Image } from '@/components/shared/Image';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useTokenImport } from '@/hooks/useTokenImport';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { useRouter } from '@/i18n/navigation';
import { useSignOut } from '@/lib/auth/useSignOut.client';
import { cn } from '@/lib/utils/cn';
import { formatAddress } from '@/lib/utils/formatAddress';
import { C_Routes } from '@/ts/constants/C_Routes';
import {
  C_TXONE_TOKEN_ADDRESS,
  C_TXONE_DECIMALS
} from '@/ts/constants/C_Staking';
import { E_Routes } from '@/ts/enums/E_Routes';

interface I_WalletDisplayProps {
  className?: string;
  onWalletClick: () => void;
  type?: 'default' | 'reward';
}

export function WalletDisplay({
  className = '',
  onWalletClick,
  type
}: I_WalletDisplayProps) {
  const isReward = type === 'reward';
  const t = useTranslations('wallet');
  const tPools = useTranslations('pools');
  const router = useRouter();
  const { isFullyLoggedIn, address, isLoading, connector } =
    useWalletConnection();
  const signOut = useSignOut();
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Token import hook
  const { importTokenToWallet, isImporting } = useTokenImport({
    tokenAddress: C_TXONE_TOKEN_ADDRESS,
    tokenSymbol: 'TxONE',
    fallbackDecimals: C_TXONE_DECIMALS
  });

  const handleImportToken = useCallback(async () => {
    await importTokenToWallet();
  }, [importTokenToWallet]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      router.push(C_Routes[E_Routes.HOME].link);
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  }, [signOut, router]);

  if (isLoading && !isReward) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className="w-4 h-4 aspect-[1/1] rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
        <span className="text-sm text-muted-foreground">{t('connecting')}</span>
      </div>
    );
  }

  if (isFullyLoggedIn && address) {
    const connectorIcon =
      hasMounted && connector?.icon ? (
        <Image src={connector.icon} alt="Wallet" />
      ) : (
        <WalletConnectBlue className="h-4 w-4 text-bg" />
      );

    // Show dropdown for reward type
    if (isReward) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              className={cn('transition-colors', className)}
              icon={
                <div className="relative h-4 w-4 mr-2">{connectorIcon}</div>
              }
            >
              {formatAddress(address)}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-[180px] bg-background"
          >
            <DropdownMenuItem
              onClick={handleImportToken}
              disabled={isImporting}
              className="cursor-pointer"
            >
              {isImporting ? 'Importing...' : tPools('importTokenInWallet')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleSignOut}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              {t('signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    // Default behavior for non-reward type
    return (
      <Button
        variant="secondary"
        onClick={onWalletClick}
        className={cn('transition-colors', className)}
        icon={<div className="relative h-4 w-4 mr-2">{connectorIcon}</div>}
      >
        {formatAddress(address)}
      </Button>
    );
  }
  return (
    <Activity mode={!isReward ? 'visible' : 'hidden'}>
      <Button
        variant="secondary"
        onClick={onWalletClick}
        className={cn(
          'min-w-33 text-base font-bold transition-colors flex items-center justify-center gap-2 rounded-lg',
          className
        )}
        icon={<WalletBlackIcon className="h-6 w-6 align-middle" />}
        iconColor="#f33"
      >
        {t('connectTitle')}
      </Button>
    </Activity>
  );
}
